import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { trpc, trpcUntypedClient } from '../lib/api';
import {
  Card,
  Descriptions,
  Button,
  Alert,
  Empty,
  Input,
  Space,
  Typography,
  Tag,
  Spin,
  message,
  Row,
  Col,
  Select,
} from 'antd';
import { NumericInput, withDevModalFilePath } from '../components/common';
import { ResponsiveTable, type ResponsiveColumnType } from '../components/common/responsive';
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  DownloadOutlined,
  FilePdfOutlined,
  InboxOutlined,
  EditOutlined,
  PrinterOutlined,
} from '@ant-design/icons';
import { getMutationPendingState } from '../utils/trpcMutation';
import { useAuthStore } from '../stores/authStore';
import { PURCHASE_ORDER_APPROVAL_SETTINGS_URL } from '../constants/purchaseOrderApprovalSettings';
import {
  calculatePurchaseOrderTaxIncludedSummary,
  formatPurchaseOrderMoney,
} from '../utils/purchaseOrderTaxIncludedSummary';

const { Title, Text, Link } = Typography;
const { TextArea } = Input;
const { Modal } = withDevModalFilePath('client/src/pages/PurchaseOrderDetailPage.tsx');

interface OrderItem {
  item: {
    id: string;
    quantity: number;
    receivedQuantity: number;
    unitPrice: string;
    amount: string;
  };
  part: {
    id: string;
    name: string;
    displayId: string;
    unit?: string | null;
  };
}

type OrderStatus = 'draft' | 'ordered' | 'partial' | 'delivered' | 'cancelled';
type ApprovalStatus = 'not_required' | 'draft' | 'pending' | 'approved' | 'rejected';
type PdfOutputAction = 'download' | 'print';
type PdfOutputLog = {
  id: string;
  action: PdfOutputAction;
  filename: string | null;
  createdAt: string | Date;
  userName: string | null;
  userEmail: string | null;
};
type ApprovalUserSummary = {
  id: string;
  label: string;
  email?: string | null;
} | null;

const outputActionLabels: Record<PdfOutputAction, string> = {
  download: 'PDFダウンロード',
  print: 'PDF印刷',
};

const approvalStatusView: Record<ApprovalStatus, { label: string; color: string }> = {
  not_required: { label: '承認不要', color: 'default' },
  draft: { label: '承認依頼前', color: 'default' },
  pending: { label: '承認待ち', color: 'warning' },
  approved: { label: '承認済み', color: 'success' },
  rejected: { label: '差し戻し', color: 'error' },
};

const getApprovalStatus = (order: unknown): ApprovalStatus => {
  const status = (order as { approvalStatus?: string | null })?.approvalStatus;
  return status && status in approvalStatusView ? status as ApprovalStatus : 'not_required';
};

const formatOutputLogTime = (value: string | Date) =>
  new Date(value).toLocaleString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });

export function PurchaseOrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [messageApi, contextHolder] = message.useMessage();
  const [receivingQuantities, setReceivingQuantities] = useState<Record<string, number>>({});
  const [isReceiveModalVisible, setIsReceiveModalVisible] = useState(false);
  const [isStatusModalVisible, setIsStatusModalVisible] = useState(false);
  const [newStatus, setNewStatus] = useState<OrderStatus | ''>('');
  const [approvalComment, setApprovalComment] = useState('');
  const [isApprovalActionLoading, setIsApprovalActionLoading] = useState(false);
  const [outputHistory, setOutputHistory] = useState<PdfOutputLog[]>([]);
  const [isOutputHistoryLoading, setIsOutputHistoryLoading] = useState(false);
  const [outputHistoryError, setOutputHistoryError] = useState<string | null>(null);

  // 発注詳細取得
  const { data, isLoading, refetch } = trpc.purchaseOrders.getById.useQuery(
    { id: id! },
    { enabled: !!id }
  );

  useEffect(() => {
    if (!id) return;
    let isMounted = true;
    setIsOutputHistoryLoading(true);
    setOutputHistoryError(null);

    void (async () => {
      try {
        const logs = await trpcUntypedClient.query('purchaseOrders.getPdfOutputLogs', {
          id,
        }) as PdfOutputLog[];
        if (!isMounted) return;
        setOutputHistory(logs);
      } catch (error) {
        if (!isMounted) return;
        const messageText = error instanceof Error ? error.message : '出力履歴の取得に失敗しました';
        setOutputHistoryError(messageText);
        setOutputHistory([]);
      } finally {
        if (isMounted) {
          setIsOutputHistoryLoading(false);
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [id]);

  // 納品処理mutation
  const receiveMutation = trpc.purchaseOrders.receive.useMutation({
    onSuccess: () => {
      messageApi.success('納品処理を完了しました');
      setIsReceiveModalVisible(false);
      setReceivingQuantities({});
      refetch();
    },
    onError: (error) => {
      messageApi.error(`納品処理に失敗しました: ${error.message}`);
    },
  });

  // ステータス更新mutation
  const updateStatusMutation = trpc.purchaseOrders.updateStatus.useMutation({
    onSuccess: () => {
      messageApi.success('ステータスを更新しました');
      setIsStatusModalVisible(false);
      setNewStatus('');
      refetch();
    },
    onError: (error) => {
      messageApi.error(`ステータス更新に失敗しました: ${error.message}`);
    },
  });

  if (isLoading || !data) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  const { order, supplier, items } = data;
  const normalizedItems: OrderItem[] = (items ?? []).map((entry) => ({
    item: {
      id: entry.item?.id ?? '',
      quantity: entry.item?.quantity ?? 0,
      receivedQuantity: entry.item?.receivedQuantity ?? 0,
      unitPrice: entry.item?.unitPrice ?? '0',
      amount: entry.item?.amount ?? '0',
    },
    part: {
      id: entry.part?.id ?? '',
      name: entry.part?.name ?? '',
      displayId: entry.part?.displayId ?? '',
      unit: entry.part?.unit ?? null,
    },
  }));
  const amountSummary = calculatePurchaseOrderTaxIncludedSummary(order, normalizedItems);

  // ステータスタグ
  const getStatusTag = (status: string) => {
    const statusMap: Record<string, { text: string; color: string }> = {
      draft: { text: '下書き', color: 'default' },
      ordered: { text: '発注済み', color: 'processing' },
      partial: { text: '部分納品', color: 'warning' },
      delivered: { text: '納品済み', color: 'success' },
      cancelled: { text: 'キャンセル', color: 'error' },
    };

    const config = statusMap[status] || statusMap.draft;
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  // 納品処理実行
  const handleReceive = () => {
    const receiveItems = Object.entries(receivingQuantities)
      .filter(([_, qty]) => qty > 0)
      .map(([itemId, receivedQuantity]) => ({
        itemId,
        receivedQuantity,
      }));

    if (receiveItems.length === 0) {
      messageApi.warning('受領数量を入力してください');
      return;
    }

    receiveMutation.mutate({
      orderId: order.id,
      items: receiveItems,
    });
  };

  // ステータス更新実行
  const handleUpdateStatus = () => {
    if (!newStatus) {
      messageApi.warning('ステータスを選択してください');
      return;
    }

    updateStatusMutation.mutate({
      id: order.id,
      status: newStatus,
    });
  };

  // 利用可能なステータス遷移
  const getAvailableStatuses = (currentStatus: OrderStatus) => {
    const validTransitions: Record<OrderStatus, Array<{ value: OrderStatus; label: string }>> = {
      draft: [
        { value: 'ordered', label: '発注済み' },
        { value: 'cancelled', label: 'キャンセル' },
      ],
      ordered: [
        { value: 'partial', label: '部分納品' },
        { value: 'delivered', label: '納品済み' },
        { value: 'cancelled', label: 'キャンセル' },
      ],
      partial: [
        { value: 'delivered', label: '納品済み' },
        { value: 'cancelled', label: 'キャンセル' },
      ],
      delivered: [],
      cancelled: [],
    };

    return validTransitions[currentStatus] || [];
  };

  // テーブルカラム定義
  const columns: ResponsiveColumnType<OrderItem>[] = [
    {
      title: '部品・消耗品ID',
      dataIndex: ['part', 'displayId'],
      key: 'partDisplayId',
      width: 120,
      mobileRole: 'tag',
      mobilePriority: 2,
    },
    {
      title: '部品・消耗品名',
      dataIndex: ['part', 'name'],
      key: 'partName',
      render: (text) => <Text strong>{text}</Text>,
      mobileRole: 'title',
      mobilePriority: 1,
    },
    {
      title: '発注数',
      dataIndex: ['item', 'quantity'],
      key: 'quantity',
      width: 100,
      align: 'right',
      render: (qty, record) => `${qty} ${record.part.unit ?? '個'}`,
      mobileRole: 'description',
      mobilePriority: 3,
    },
    {
      title: '受領済',
      dataIndex: ['item', 'receivedQuantity'],
      key: 'receivedQuantity',
      width: 100,
      align: 'right',
      render: (qty, record) => `${qty} ${record.part.unit ?? '個'}`,
      mobileRole: 'tag',
      mobilePriority: 3,
    },
    {
      title: '単価(税込)',
      dataIndex: ['item', 'unitPrice'],
      key: 'unitPrice',
      width: 120,
      align: 'right',
      render: (price) => `¥${Number.parseFloat(String(price ?? 0)).toLocaleString()}`,
      mobileRole: 'description',
      mobilePriority: 4,
    },
    {
      title: '金額(税込)',
      dataIndex: ['item', 'amount'],
      key: 'amount',
      width: 120,
      align: 'right',
      render: (amount) => `¥${Number.parseFloat(String(amount ?? 0)).toLocaleString()}`,
      mobileRole: 'tag',
      mobilePriority: 4,
    },
  ];

  // 納品モーダル用カラム
  const receiveColumns: ResponsiveColumnType<OrderItem>[] = [
    ...columns,
    {
      title: '今回受領数',
      key: 'receiving',
      width: 150,
      render: (_, record) => {
        const remaining = record.item.quantity - record.item.receivedQuantity;
        return (
            <NumericInput
              min={0}
              max={remaining}
              value={receivingQuantities[record.item.id] || 0}
              onChange={(value) => {
                const next = typeof value === 'number' ? value : value ? Number(value) : 0;
                setReceivingQuantities({
                  ...receivingQuantities,
                  [record.item.id]: Number.isFinite(next) ? next : 0,
                });
              }}
              addonAfter={record.part.unit ?? '個'}
            />
        );
      },
      mobileRole: 'description',
      mobilePriority: 5,
    },
  ];

  const canReceive = order.status === 'ordered' || order.status === 'partial';
  const availableStatuses = getAvailableStatuses(order.status as OrderStatus);
  const canChangeStatus = availableStatuses.length > 0;
  const pdfFileName = (data as { pdfFilename: string }).pdfFilename;
  const approvalRequired = Boolean((order as { approvalRequired?: boolean | null }).approvalRequired);
  const approvalStatus = getApprovalStatus(order);
  const approvalView = approvalStatusView[approvalStatus];
  const isFormalPdfBlocked = approvalRequired && approvalStatus !== 'approved';
  const canManageApproval = ['admin', 'manager'].includes(user?.role ?? '');
  const approvalAssignee = (data as { approvalAssignee?: ApprovalUserSummary }).approvalAssignee ?? null;
  const approvalRequestedByUser =
    (data as { approvalRequestedByUser?: ApprovalUserSummary }).approvalRequestedByUser ?? null;
  const approvedByUser = (data as { approvedByUser?: ApprovalUserSummary }).approvedByUser ?? null;
  const approvalRequestComment =
    (data as { approvalRequestComment?: string | null }).approvalRequestComment ?? null;
  const approvalDecisionComment =
    approvalStatus === 'approved'
      ? ((order as { approvalComment?: string | null }).approvalComment?.trim() || null)
      : null;
  const isAssignedApprover =
    !((order as { approvalAssigneeId?: string | null }).approvalAssigneeId)
    || (order as { approvalAssigneeId?: string | null }).approvalAssigneeId === user?.id;
  const canSubmitApproval =
    approvalRequired
    && order.status === 'draft'
    && ['draft', 'rejected'].includes(approvalStatus);
  const canApproveApproval = approvalRequired && approvalStatus === 'pending' && canManageApproval && isAssignedApprover;
  const approvedByLabel = approvedByUser?.label ?? (order as { approvedBy?: string | null }).approvedBy ?? '-';
  const approvedAt = (order as { approvedAt?: string | Date | null }).approvedAt ?? null;
  const rejectionReason = (order as { rejectionReason?: string | null }).rejectionReason ?? null;
  const handleOpenPdf = () => navigate(`/app/purchase-orders/${order.id}/pdf`);
  const handlePrint = () => {
    if (isFormalPdfBlocked) {
      messageApi.warning('承認済みになるまで正式PDFを出力できません');
      return;
    }
    window.open(`/print/purchase-orders/${order.id}`, '_blank', 'noopener,noreferrer');
  };

  const handleApprovalAction = async (action: 'submit' | 'approve' | 'reject') => {
    const trimmedComment = approvalComment.trim();
    if (action === 'reject' && !trimmedComment) {
      messageApi.warning('差し戻し理由を入力してください');
      return;
    }

    const endpoint = {
      submit: 'purchaseOrders.submitForApproval',
      approve: 'purchaseOrders.approve',
      reject: 'purchaseOrders.reject',
    }[action];
    const payload = action === 'reject'
      ? { id: order.id, reason: trimmedComment }
      : { id: order.id, comment: trimmedComment || undefined };
    const successMessage = {
      submit: '承認依頼を送りました',
      approve: '発注を承認しました',
      reject: '発注を差し戻しました',
    }[action];

    setIsApprovalActionLoading(true);
    try {
      await trpcUntypedClient.mutation(endpoint, payload);
      messageApi.success(successMessage);
      setApprovalComment('');
      await refetch();
    } catch (error) {
      const messageText = error instanceof Error ? error.message : '承認操作に失敗しました';
      messageApi.error(messageText);
    } finally {
      setIsApprovalActionLoading(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      {contextHolder}

      {/* ヘッダー */}
      <div style={{ marginBottom: 24 }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/app/purchase-orders')}
          style={{ marginBottom: 16 }}
        >
          発注一覧に戻る
        </Button>
        <Space align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
          <div>
            <Title level={2} style={{ margin: 0 }}>発注詳細</Title>
            <Text type="secondary">{order.displayId}</Text>
          </div>
          <Space>
            {canChangeStatus && (
              <Button
                icon={<EditOutlined />}
                onClick={() => {
                  setNewStatus('');
                  setIsStatusModalVisible(true);
                }}
              >
                ステータス変更
              </Button>
            )}
            <Button
              icon={<DownloadOutlined aria-hidden="true" />}
              onClick={handleOpenPdf}
            >
              PDFをダウンロード
            </Button>
            <Button
              icon={<PrinterOutlined aria-hidden="true" />}
              disabled={isFormalPdfBlocked}
              onClick={handlePrint}
            >
              印刷
            </Button>
            {canReceive && (
              <Button
                type="primary"
                icon={<InboxOutlined />}
                onClick={() => {
                  // 未受領数を初期値として設定
                  const initial: Record<string, number> = {};
                  normalizedItems.forEach((item) => {
                    const remaining = item.item.quantity - item.item.receivedQuantity;
                    if (remaining > 0) {
                      initial[item.item.id] = remaining;
                    }
                  });
                  setReceivingQuantities(initial);
                  setIsReceiveModalVisible(true);
                }}
              >
                納品処理
              </Button>
            )}
          </Space>
        </Space>
      </div>

      <Space direction="vertical" size={16} style={{ width: '100%' }}>
      {/* 発注情報 */}
      <Card title="発注情報">
        <Descriptions bordered column={2}>
          <Descriptions.Item label="発注ID">{order.displayId}</Descriptions.Item>
          <Descriptions.Item label="ステータス">{getStatusTag(order.status)}</Descriptions.Item>
          {approvalRequired && (
            <Descriptions.Item label="承認状態">
              <Tag color={approvalView.color}>{approvalView.label}</Tag>
            </Descriptions.Item>
          )}
          <Descriptions.Item label="業者">{supplier.name}</Descriptions.Item>
          <Descriptions.Item label="業者ID">{supplier.displayId}</Descriptions.Item>
          <Descriptions.Item label="発注日">
            {new Date(order.orderDate).toLocaleDateString('ja-JP')}
          </Descriptions.Item>
          <Descriptions.Item label="納品予定日">
            {order.expectedDeliveryDate
              ? new Date(order.expectedDeliveryDate).toLocaleDateString('ja-JP')
              : '-'}
          </Descriptions.Item>
          {order.actualDeliveryDate && (
            <Descriptions.Item label="実際の納品日" span={2}>
              {new Date(order.actualDeliveryDate).toLocaleDateString('ja-JP')}
            </Descriptions.Item>
          )}
          <Descriptions.Item label="備考" span={2}>
            {order.notes || '-'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {approvalRequired && (
        <Card
          title="発注承認"
          extra={<Link href={PURCHASE_ORDER_APPROVAL_SETTINGS_URL}>部品発注設定を開く</Link>}
        >
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <Alert
              type={isFormalPdfBlocked ? 'warning' : 'success'}
              showIcon
              message={
                <Space wrap>
                  <Tag color={approvalView.color}>{approvalView.label}</Tag>
                  <Text>
                    {isFormalPdfBlocked
                      ? '承認済みになるまで正式PDFを出力できません'
                      : '承認済みの正式PDFとして出力できます'}
                  </Text>
                </Space>
              }
            />
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="承認先">{approvalAssignee?.label ?? '-'}</Descriptions.Item>
              <Descriptions.Item label="承認依頼者">{approvalRequestedByUser?.label ?? '-'}</Descriptions.Item>
              <Descriptions.Item label="承認者">{approvalStatus === 'approved' ? approvedByLabel : '-'}</Descriptions.Item>
              <Descriptions.Item label="承認日">
                {approvedAt ? new Date(approvedAt).toLocaleString('ja-JP') : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="差し戻し理由" span={2}>
                {rejectionReason ?? '-'}
              </Descriptions.Item>
              {approvalRequestComment && (
                <Descriptions.Item label="承認依頼コメント" span={2}>
                  <Text style={{ whiteSpace: 'pre-wrap' }}>{approvalRequestComment}</Text>
                </Descriptions.Item>
              )}
              {approvalDecisionComment && (
                <Descriptions.Item label="承認コメント" span={2}>
                  <Text style={{ whiteSpace: 'pre-wrap' }}>{approvalDecisionComment}</Text>
                </Descriptions.Item>
              )}
            </Descriptions>
            <TextArea
              rows={3}
              value={approvalComment}
              onChange={(event) => setApprovalComment(event.target.value)}
              placeholder={approvalStatus === 'pending' ? '承認コメントまたは差し戻し理由' : '承認依頼コメント'}
              disabled={isApprovalActionLoading}
            />
            <Space wrap>
              <Button
                type="primary"
                loading={isApprovalActionLoading}
                disabled={!canSubmitApproval}
                onClick={() => void handleApprovalAction('submit')}
              >
                承認依頼を送る
              </Button>
              {canManageApproval && (
                <>
                  <Button
                    type="primary"
                    loading={isApprovalActionLoading}
                    disabled={!canApproveApproval}
                    onClick={() => void handleApprovalAction('approve')}
                  >
                    発注を承認する
                  </Button>
                  <Button
                    danger
                    loading={isApprovalActionLoading}
                    disabled={!canApproveApproval}
                    onClick={() => void handleApprovalAction('reject')}
                  >
                    差し戻す
                  </Button>
                </>
              )}
            </Space>
          </Space>
        </Card>
      )}

      {/* 発注明細 */}
      <Card title="発注明細">
          <ResponsiveTable<OrderItem>
            columns={columns}
            dataSource={normalizedItems}
            pagination={false}
            rowKey={(record) => record.item.id}
            mobileCardConfig={{
              titleField: (record) => record.part?.name ?? '',
            }}
          />
      </Card>

      {/* 金額サマリー */}
      <Card title="金額" data-testid="purchase-order-amount-section">
        <Row>
          <Col span={12}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Text>小計(税込):</Text>
                <Text>{formatPurchaseOrderMoney(amountSummary.subtotal)}</Text>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text>内消費税:</Text>
                <Text>{formatPurchaseOrderMoney(amountSummary.tax)}</Text>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Text>送料(税込):</Text>
                <Text>{formatPurchaseOrderMoney(amountSummary.shippingCost)}</Text>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 8, borderTop: '1px solid #f0f0f0' }}>
                <Text strong style={{ fontSize: 16 }}>税込合計:</Text>
                <Text strong style={{ fontSize: 18 }}>
                  {formatPurchaseOrderMoney(amountSummary.totalAmount)}
                </Text>
              </div>
            </Space>
          </Col>
        </Row>
      </Card>

          <Card
            title="発注書PDFと出力履歴"
            data-testid="purchase-order-pdf-history-section"
            data-layout="single-column"
          >
            <section
              aria-label="PDF出力"
              style={{
                border: '1px solid #f0f0f0',
                borderRadius: 8,
                padding: 16,
                marginBottom: 16,
              }}
            >
              <Typography.Title level={5} style={{ marginTop: 0 }}>
                PDF出力
              </Typography.Title>
              <Space direction="vertical" size={6}>
                <Text>{pdfFileName}</Text>
                <Text type="secondary">
                  {isFormalPdfBlocked
                    ? '承認済みになるまで正式PDFは作成できません'
                    : 'PDF確認画面で作成・ダウンロードできます'}
                </Text>
                <Button icon={<FilePdfOutlined aria-hidden="true" />} onClick={handleOpenPdf}>
                  発注書PDFを開く
                </Button>
              </Space>
            </section>

            <Typography.Title level={5}>出力履歴</Typography.Title>
            {isOutputHistoryLoading ? (
              <div style={{ padding: '20px 0', textAlign: 'center' }}>
                <Spin size="small" />
              </div>
            ) : outputHistoryError ? (
              <Text type="danger">{outputHistoryError}</Text>
            ) : outputHistory.length === 0 ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="出力履歴はまだありません" />
            ) : (
              <Space direction="vertical" size={0} style={{ width: '100%' }}>
                {outputHistory.map((entry) => (
                  <Row
                    key={entry.id}
                    align="middle"
                    justify="space-between"
                    style={{ borderBottom: '1px solid #f0f0f0', padding: '10px 0' }}
                  >
                    <Col>
                      <Space align="start">
                        <CheckCircleOutlined style={{ color: '#389e0d', marginTop: 3 }} />
                        <Space direction="vertical" size={0}>
                          <Text strong>{outputActionLabels[entry.action]}</Text>
                          <Text type="secondary">{entry.filename ?? pdfFileName}</Text>
                          <Text type="secondary">
                            {entry.userName ?? entry.userEmail ?? '実行者不明'}
                          </Text>
                        </Space>
                      </Space>
                    </Col>
                    <Col>
                      <Text>{formatOutputLogTime(entry.createdAt)}</Text>
                    </Col>
                  </Row>
                ))}
              </Space>
            )}

            <section
              aria-label="次の作業"
              style={{
                border: '1px solid #f0f0f0',
                borderRadius: 8,
                padding: 16,
                marginTop: 16,
              }}
            >
              <Typography.Title level={5} style={{ marginTop: 0 }}>
                次の作業
              </Typography.Title>
              <Space direction="vertical" size={8}>
                <Button
                  type="primary"
                  icon={<InboxOutlined aria-hidden="true" />}
                  disabled={!canReceive}
                  onClick={() => {
                    const initial: Record<string, number> = {};
                    normalizedItems.forEach((item) => {
                      const remaining = item.item.quantity - item.item.receivedQuantity;
                      if (remaining > 0) {
                        initial[item.item.id] = remaining;
                      }
                    });
                    setReceivingQuantities(initial);
                    setIsReceiveModalVisible(true);
                  }}
                >
                  納品処理を開始
                </Button>
              </Space>
            </section>
          </Card>
      </Space>

      {/* 納品処理モーダル */}
      <Modal
        title="納品処理"
        open={isReceiveModalVisible}
        onCancel={() => setIsReceiveModalVisible(false)}
        onOk={handleReceive}
        confirmLoading={getMutationPendingState(receiveMutation)}
        width={900}
        okText="納品完了"
        cancelText="キャンセル"
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
          受領した数量を入力してください。在庫が自動的に更新されます。
        </Text>
        <ResponsiveTable<OrderItem>
          columns={receiveColumns}
          dataSource={normalizedItems}
          pagination={false}
          rowKey={(record) => record.item.id}
          mobileCardConfig={{
            titleField: (record) => record.part?.name ?? '',
          }}
        />
      </Modal>

      {/* ステータス変更モーダル */}
      <Modal
        title="ステータス変更"
        open={isStatusModalVisible}
        onCancel={() => setIsStatusModalVisible(false)}
        onOk={handleUpdateStatus}
        confirmLoading={getMutationPendingState(updateStatusMutation)}
        okText="変更"
        cancelText="キャンセル"
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <div>
            <Text strong>現在のステータス: </Text>
            {getStatusTag(order.status)}
          </div>
          <div style={{ marginTop: 16 }}>
            <Text strong>変更先のステータス</Text>
            <Select
              style={{ width: '100%', marginTop: 8 }}
              placeholder="ステータスを選択"
              value={newStatus || undefined}
              onChange={(value) => setNewStatus(value as OrderStatus)}
              options={availableStatuses}
            />
          </div>
        </Space>
      </Modal>
    </div>
  );
}
